// Repository layer: executor-agnostic data access over the local encrypted SQLite store.
// Pure functions that take an `exec` (see db.makeExecutor) so they are unit-testable with
// an in-memory sql.js executor. No React, no network here.
import { RECORD_TABLES } from "./db";

const TABLES = new Set(RECORD_TABLES);
function assertTable(t) { if (!TABLES.has(t)) throw new Error(`Unknown table ${t}`); }

// Stable record identity. Multi-record collections carry `id`; single-doc collections
// (settings/accountant_profile) fall back to `user_id`. Exported so the seed/self-heal layer
// derives server ids with EXACTLY the same semantics the store uses to key rows.
export function recordId(rec) {
  if (rec.id != null) return String(rec.id);
  if (rec.user_id != null) return String(rec.user_id); // single-doc collections (settings/accountant)
  return null;
}

export async function upsertRecord(exec, table, rec, opts = {}) {
  assertTable(table);
  const id = recordId(rec);
  if (!id) return;
  const pending = opts.pending ? 1 : 0;
  const srvDeleted = rec.deleted_at ? 1 : (opts.srvDeleted ? 1 : 0);
  const ts = rec.updated_at || rec.created_at || new Date().toISOString();
  await exec.run(
    `INSERT INTO ${table} (id,user_id,data,updated_at,pending,srv_deleted) VALUES (?,?,?,?,?,?)
     ON CONFLICT(id) DO UPDATE SET user_id=excluded.user_id, data=excluded.data,
       updated_at=excluded.updated_at, pending=excluded.pending, srv_deleted=excluded.srv_deleted`,
    [id, rec.user_id != null ? String(rec.user_id) : null, JSON.stringify(rec), ts, pending, srvDeleted],
  );
}

export async function upsertMany(exec, table, rows, opts = {}) {
  for (const r of rows || []) await upsertRecord(exec, table, r, opts);
}

export async function getRecord(exec, table, id) {
  assertTable(table);
  const rows = await exec.all(`SELECT data FROM ${table} WHERE id=?`, [String(id)]);
  return rows.length ? JSON.parse(rows[0].data) : null;
}

export async function removeRecord(exec, table, id) {
  assertTable(table);
  await exec.run(`DELETE FROM ${table} WHERE id=?`, [String(id)]);
}

export async function listRecords(exec, table, userId) {
  assertTable(table);
  const rows = userId
    ? await exec.all(`SELECT data FROM ${table} WHERE user_id=?`, [String(userId)])
    : await exec.all(`SELECT data FROM ${table}`, []);
  return rows.map((r) => JSON.parse(r.data));
}

export async function countRecords(exec, table, userId) {
  assertTable(table);
  const rows = userId
    ? await exec.all(`SELECT COUNT(*) as n FROM ${table} WHERE user_id=?`, [String(userId)])
    : await exec.all(`SELECT COUNT(*) as n FROM ${table}`, []);
  return rows.length ? Number(rows[0].n) : 0;
}

// Stable record ids for a table scoped to the user. Used by the self-heal completeness check
// to compare server-id membership against local-id membership (not counts).
export async function listRecordIds(exec, table, userId) {
  assertTable(table);
  const rows = userId
    ? await exec.all(`SELECT id FROM ${table} WHERE user_id=?`, [String(userId)])
    : await exec.all(`SELECT id FROM ${table}`, []);
  return rows.map((r) => String(r.id));
}

// ---------------- Reset (local-first data wipe) ----------------

// Wipe ALL of the user's local Molaroo data so a server-side Reset Data cannot reappear from
// the device. Clears every record table, the outbox, the agreement-refresh trust markers,
// the seed/migration state, and the API response cache — EXCEPT any cache urls the caller asks
// to preserve (identity/billing) so the authenticated session survives the reset. Transactional.
export async function clearAllLocalData(exec, opts = {}) {
  const preserve = opts.preserveCacheUrls || [];
  const keep = (url) => preserve.some((p) => url === p || url.startsWith(p));
  await exec.transaction(async () => {
    for (const t of RECORD_TABLES) await exec.run(`DELETE FROM ${t}`, []);
    await exec.run(`DELETE FROM outbox`, []);
    await exec.run(`DELETE FROM agreement_refresh`, []);
    await exec.run(`DELETE FROM migration_state`, []);
    const rows = await exec.all(`SELECT url FROM api_cache`, []);
    for (const r of rows) if (!keep(r.url)) await exec.run(`DELETE FROM api_cache WHERE url=?`, [r.url]);
  });
}

// ---------------- Full-URL response cache (offline read fidelity) ----------------

export async function setCache(exec, url, data) {
  await exec.run(
    `INSERT INTO api_cache (url,response,cached_at) VALUES (?,?,?)
     ON CONFLICT(url) DO UPDATE SET response=excluded.response, cached_at=excluded.cached_at`,
    [url, JSON.stringify(data), new Date().toISOString()],
  );
}

export async function getCache(exec, url) {
  const rows = await exec.all(`SELECT response FROM api_cache WHERE url=?`, [url]);
  return rows.length ? JSON.parse(rows[0].response) : undefined;
}

// Patch every cached list (array) under a path prefix so offline lists reflect a local write.
export async function patchCaches(exec, prefix, patch) {
  const rows = await exec.all(`SELECT url,response FROM api_cache WHERE url LIKE ?`, [prefix + "%"]);
  for (const row of rows) {
    let arr;
    try { arr = JSON.parse(row.response); } catch { continue; }
    if (!Array.isArray(arr)) continue;
    if (patch.op === "remove") {
      arr = arr.filter((x) => String(x && x.id) !== String(patch.id));
    } else {
      const idx = arr.findIndex((x) => String(x && x.id) === String(patch.item.id));
      if (idx >= 0) arr[idx] = { ...arr[idx], ...patch.item };
      else arr.unshift(patch.item);
    }
    await setCache(exec, row.url, arr);
  }
}

// ---------------- Agreement reconciliation (authoritative, per-practice) ----------------

export async function markAgreementsRefreshed(exec, userId, practiceId) {
  if (!practiceId) return;
  await exec.run(
    `INSERT INTO agreement_refresh (id,user_id,refreshed_at) VALUES (?,?,?)
     ON CONFLICT(id) DO UPDATE SET user_id=excluded.user_id, refreshed_at=excluded.refreshed_at`,
    [String(practiceId), userId != null ? String(userId) : null, new Date().toISOString()],
  );
}

export async function isAgreementRefreshed(exec, userId, practiceId) {
  if (!practiceId) return false;
  const rows = await exec.all(`SELECT refreshed_at FROM agreement_refresh WHERE id=?`, [String(practiceId)]);
  return rows.length > 0;
}

// Build 12: return WHEN a practice's agreements were last authoritatively reconciled (ISO),
// or null if never. The gateway compares this against the current app-session start so a
// PERSISTENT marker from a prior session can no longer bless now-stale local agreement data.
export async function agreementRefreshedAt(exec, practiceId) {
  if (!practiceId) return null;
  const rows = await exec.all(`SELECT refreshed_at FROM agreement_refresh WHERE id=?`, [String(practiceId)]);
  return rows.length ? rows[0].refreshed_at : null;
}

// Transactionally REPLACE a practice's local agreement_versions with the authoritative server
// set (removes stale/superseded rows, including old effective_end=null rows) and marks it
// trusted. Only touches agreement_versions for THIS practice — never other practices, never
// Work/Payments/Reports/Expenses or any financial record. Idempotent and interrupt-safe.
export async function replacePracticeAgreements(exec, userId, practiceId, versions) {
  if (!practiceId) return;
  await exec.transaction(async () => {
    const rows = await exec.all(`SELECT id, data FROM agreement_versions WHERE user_id=?`, [String(userId)]);
    for (const r of rows) {
      let d; try { d = JSON.parse(r.data); } catch { continue; }
      if (String(d.practice_id) === String(practiceId)) await exec.run(`DELETE FROM agreement_versions WHERE id=?`, [String(r.id)]);
    }
    for (const v of versions || []) if (v && v.id) await upsertRecord(exec, "agreement_versions", v);
    await exec.run(
      `INSERT INTO agreement_refresh (id,user_id,refreshed_at) VALUES (?,?,?)
       ON CONFLICT(id) DO UPDATE SET user_id=excluded.user_id, refreshed_at=excluded.refreshed_at`,
      [String(practiceId), userId != null ? String(userId) : null, new Date().toISOString()],
    );
  });
}

// ---------------- Outbox (persistent server-mirror queue) ----------------

export async function enqueue(exec, { key, method, url, body, meta }) {
  const now = new Date().toISOString();
  await exec.run(
    `INSERT INTO outbox (key,method,url,body,meta,status,attempts,created_at,updated_at)
     VALUES (?,?,?,?,?, 'pending', 0, ?, ?)`,
    [key, method, url, JSON.stringify(body ?? {}), JSON.stringify(meta ?? {}), now, now],
  );
}

export async function listPending(exec) {
  const rows = await exec.all(`SELECT * FROM outbox WHERE status='pending' ORDER BY created_at ASC`, []);
  return rows.map((r) => ({ ...r, body: JSON.parse(r.body || "{}"), meta: JSON.parse(r.meta || "{}") }));
}

// If an unsynced create for the same entity exists, fold later edits into its body
// (prevents a PUT/DELETE referencing a server id the server does not know yet).
export async function findPendingCreate(exec, entity, entityId) {
  const rows = await exec.all(
    `SELECT * FROM outbox WHERE status='pending' AND method='POST' AND meta LIKE ?`,
    [`%"entityId":"${entityId}"%`],
  );
  const match = rows.find((r) => { try { return JSON.parse(r.meta).entity === entity; } catch { return false; } });
  return match ? { ...match, body: JSON.parse(match.body || "{}"), meta: JSON.parse(match.meta || "{}") } : null;
}

export async function updateOutboxBody(exec, key, body) {
  await exec.run(`UPDATE outbox SET body=?, updated_at=? WHERE key=?`,
    [JSON.stringify(body ?? {}), new Date().toISOString(), key]);
}

export async function removeOutbox(exec, key) {
  await exec.run(`DELETE FROM outbox WHERE key=?`, [key]);
}

export async function markSynced(exec, key) {
  await exec.run(`UPDATE outbox SET status='synced', updated_at=? WHERE key=?`,
    [new Date().toISOString(), key]);
}

export async function markFailed(exec, key, err) {
  await exec.run(
    `UPDATE outbox SET status='failed', last_error=?, attempts=attempts+1, updated_at=? WHERE key=?`,
    [String(err || "").slice(0, 500), new Date().toISOString(), key],
  );
}

// ---------------- Migration / seed state (per user, idempotent) ----------------

export async function isSeeded(exec, userId) {
  const rows = await exec.all(`SELECT seeded FROM migration_state WHERE user_id=?`, [String(userId)]);
  return rows.length ? Number(rows[0].seeded) === 1 : false;
}

export async function markSeeded(exec, userId, schemaVersion) {
  await exec.run(
    `INSERT INTO migration_state (user_id,seeded,seeded_at,schema_version) VALUES (?,1,?,?)
     ON CONFLICT(user_id) DO UPDATE SET seeded=1, seeded_at=excluded.seeded_at, schema_version=excluded.schema_version`,
    [String(userId), new Date().toISOString(), schemaVersion || 1],
  );
}

// ---------------- Repository reads (repository-first, offline-capable) ----------------

function agVersion(a) { return Number(a.version ?? a.version_number ?? 0); }
function pickCurrentAgreement(ags) {
  if (!ags || !ags.length) return null;
  // Agreement docs use `version` (not `version_number`) — pick the highest version, which
  // mirrors the server's current_agreement() latest-version selection.
  return [...ags].sort((a, b) =>
    (agVersion(b) - agVersion(a)) ||
    String(b.created_at || "").localeCompare(String(a.created_at || "")))[0];
}

// Practices with their current agreement joined from the local agreement_versions table,
// so practice selectors work offline straight after seeding (no server enrichment needed).
export async function readPractices(exec, userId) {
  const practices = await listRecords(exec, "practices", userId);
  const ags = await listRecords(exec, "agreement_versions", userId);
  const byPractice = {};
  for (const a of ags) (byPractice[String(a.practice_id)] = byPractice[String(a.practice_id)] || []).push(a);
  return practices.map((p) => (p.agreement
    ? p : { ...p, agreement: pickCurrentAgreement(byPractice[String(p.id)] || []) }));
}

// Generic list read with month/practice/soft-delete filtering against local structured data.
export async function readLocalList(exec, table, userId, opts = {}) {
  let rows = await listRecords(exec, table, userId);
  if (opts.deleted === true) rows = rows.filter((r) => !!r.deleted_at);
  else if (opts.deleted === false) rows = rows.filter((r) => !r.deleted_at);
  if (opts.month && opts.monthField) rows = rows.filter((r) => String(r[opts.monthField] || "").slice(0, 7) === opts.month);
  if (opts.practiceId) rows = rows.filter((r) => String(r.practice_id) === String(opts.practiceId));
  return rows;
}
